<template>
  <li
    class="drop-down-menu-item__wrapper tw-p-1"
    tabindex="0"
    :data-test="dataTest"
    @click.stop.prevent="onHandlerOption">
    <div
      :class="[
        'tw-flex tw-flex-row tw-justify-between tw-items-center',
        'tw-p-1 tw-gap-2',
        'tw-rounded tw-border-0',
        'tw-cursor-pointer',
        'hover:tw-bg-core-primary-95',
        'drop-down-menu-item',
        { 'tw-bg-core-primary-80': selected },
      ]">
      <div 
        :class="[
          'tw-flex tw-flex-row tw-grow tw-items-center tw-gap-2',
          'tw-border-0',
          'tw-py-0.5 tw-px-1'
        ]">
        <CheckboxIcon
          v-if="multiple"
          :value="selected ? CheckboxState.CHECKED : CheckboxState.UNCHECKED"
          :disabled="disabled" />
        <SVGIcon
          v-if="iconName"
          data-test="drop-down-menu-item-icon"
          :colorClass="iconColorClass"
          :iconName="iconName"
          :remSize="1.25" />
        <ImageIcon
          v-else-if="imageSrc"
          :imageSrc="imageSrc" 
          data-test="drop-down-menu-item-image-icon" />
        <span
          :class="disabled ? 'tw-text-core-neutral-cold-40' : 'tw-text-core-primary-10'"
          class="drop-down-menu-item__title body--body-01"
          data-test="drop-down-menu-item-label">
          {{ text }}
        </span>
      </div>
      <SVGIcon
        v-if="!multiple && selected"
        iconName="CheckCircle"
        :colorClass="disabled ? 'tw-fill-core-neutral-cold-50' : 'tw-fill-core-primary-30'"
        :remSize="1.25" />
    </div>
  </li>
</template>

<script setup lang="ts">
import CheckboxIcon from 'frontcore/views/selection-control/checkbox-icon/CheckboxIcon.vue'
import ImageIcon from 'frontcore/views/icons/image-icon/ImageIcon.vue'
import SVGIcon from 'frontcore/views/icons/SVGIcon.vue'
import { CheckboxState } from 'frontcore/views/selection-control/types'
import { computed } from 'vue'

interface Props {
  id: number | string
  text: string
  dataTest?: string
  disabled?: boolean
  iconColorClass?: string
  iconName?: string
  imageSrc?: string
  selected?: boolean
  multiple?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  selected: false,
  multiple: false,
})

type Emits = (e: 'onClickOption', value: Props['id']) => void

const emit = defineEmits<Emits>()

const iconColorClass = computed(() => {
  if (props.disabled) return 'tw-fill-core-neutral-cold-70'
  if (props.iconColorClass) return props.iconColorClass
  if (props.selected) return 'tw-fill-core-primary-30'
  return 'tw-fill-core-primary-50'
})

const onHandlerOption = ():void => {
  if (props.disabled) return
  emit('onClickOption', props.id)
}

</script>

<style scoped>
.drop-down-menu-item:focus {
  outline: 3px solid var(--auxiliary-info-60) !important;
  border-radius: 0.25rem;
  box-shadow: 
    0 0 4px 1px var(--core-primary-60),
    0 0 4px 0 var(--core-primary-60);
}
.drop-down-menu-item__title {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 80%;
}
</style>
